import { MLHelper, createGuid } from '@bimser/core';
import IconAdd from '@bimser/icons/16/add';
import IconDelete from '@bimser/icons/16/delete';
import IconEdit from '@bimser/icons/16/edit';
import React from 'react';
import { BCDataGrid } from '../BCDataGrid';
import { OptionHeader } from './components';
import { IBCLikertProps, IDefinition, IOption } from './entities';
import {
	calculateDefinitionColumnsDisplayValue,
	calculateOptionColumnsCellValue,
	setOptionColumnsCellValue
} from './helpers';
import { findMessage } from '../BCIntl';

export const BCLikert: React.FC<IBCLikertProps> = props => {
	const bcDataGridRef = React.useRef<BCDataGrid.Entities.BCDataGridRef>();

	const hasColumnButton = !props.hideDeleteDefinition || !props.hideEditDefinition;

	React.useEffect(() => {
		// onReorderOptions
		bcDataGridRef.current.instance().off('optionChanged');
		bcDataGridRef.current.instance().on('optionChanged', e => {
			if (e.name === 'columns' && e.fullName.endsWith('.visibleIndex')) {
				const columns = bcDataGridRef.current.instance().getVisibleColumns();

				const newOptions = props.options.map(option => {
					const column = columns.find(({ name }) => name === option.key);

					return {
						...option,
						displayOrder: column.visibleIndex - (hasColumnButton ? 2 : 1)
					};
				});

				const targetColumn = bcDataGridRef.current.instance().columnOption(e.previousValue);

				props.onOptionUpdated(
					newOptions,
					newOptions.find(({ key }) => key === targetColumn.name),
					props.options.find(({ key }) => key === targetColumn.name)
				);
			}
		});

		() => {
			bcDataGridRef.current?.instance().off('optionChanged');
		};
	}, [props.options, props.readonly, props.disabled, hasColumnButton]);

	const onClickAddOption = React.useCallback(async () => {
		const title = await props.showAddTitleModal('option');

		if (title) {
			const newOption: IOption = {
				key: createGuid(),
				displayOrder: props.options.length,
				title
			};

			const newOptions = [...props.options, newOption];

			props.onNewOptionAdded(newOptions, newOption);
		}
	}, [props.options]);

	const onClickEditOption = React.useCallback(
		async (option: IOption) => {
			const newTitle = await props.showEditTitleModal(option.title, 'option');

			if (newTitle) {
				const newOption: IOption = {
					...option,
					title: newTitle
				};

				const newOptions = props.options.map(_option => {
					if (_option.key === newOption.key) {
						return newOption;
					}

					return _option;
				});

				props.onOptionUpdated(newOptions, newOption, option);
			}
		},
		[props.options]
	);

	const onClickDeleteOption = React.useCallback(
		async (deletedOption: IOption) => {
			const result = await props.showConfirmModal();

			if (!result) {
				return;
			}

			const { displayOrder } = props.options.find(({ key }) => key === deletedOption.key);

			const newOptions = props.options
				.filter(({ key }) => key !== deletedOption.key)
				.map(option => {
					if (option.displayOrder > displayOrder) {
						return {
							...option,
							displayOrder: option.displayOrder - 1
						};
					}

					return option;
				});

			const newDefinitions = props.definitions.map(definition => {
				if (definition.selectedOptionKey === deletedOption.key) {
					return {
						...definition,
						selectedOptionKey: null
					};
				}

				return definition;
			});

			props.onOptionDeleted(newOptions, newDefinitions, deletedOption);
		},
		[props.options, props.definitions]
	);

	const onClickAddDefinition = React.useCallback(async () => {
		const title = await props.showAddTitleModal('definition');

		if (title) {
			const newDefinition: IDefinition = {
				key: createGuid(),
				displayOrder: props.definitions.length,
				selectedOptionKey: null,
				title
			};

			const newDefinitions = [...props.definitions, newDefinition];

			props.onNewDefinitionAdded(newDefinitions, newDefinition);
		}
	}, [props.definitions]);

	const onClickEditDefinition = React.useCallback(
		async e => {
			const oldDefinition = e.row.data as IDefinition;
			const newTitle = await props.showEditTitleModal(oldDefinition.title, 'definition');

			if (newTitle) {
				const newDefinition: IDefinition = {
					...oldDefinition,
					title: newTitle
				};

				const newDefinitions = props.definitions.map(definition => {
					if (definition.key === oldDefinition.key) {
						return newDefinition;
					}

					return definition;
				});

				props.onDefinitionUpdated(newDefinitions, newDefinition, oldDefinition);
			}
		},
		[props.definitions]
	);

	const onClickDeleteDefinition = React.useCallback(
		async e => {
			const result = await props.showConfirmModal();

			if (!result) {
				return;
			}

			const newDefinitions = props.definitions.filter(
				definition => definition.key !== e.row.data.key
			);

			props.onDefinitionDeleted(newDefinitions, e.row.data);
		},
		[props.definitions]
	);

	const onReorderDefinitions = React.useCallback(
		e => {
			const fromIndex: number = e.fromIndex;
			const toIndex: number = e.toIndex;
			const itemData: IDefinition = e.itemData;

			let newDefinition!: IDefinition;

			const newDefinitions = props.definitions.map(definition => {
				const updatedDefinition = { ...definition };

				if (definition.key === itemData.key) {
					updatedDefinition.displayOrder = toIndex;
					newDefinition = updatedDefinition;

					return updatedDefinition;
				}

				if (
					fromIndex > toIndex &&
					definition.displayOrder >= toIndex &&
					definition.displayOrder <= fromIndex
				) {
					updatedDefinition.displayOrder++;
				} else if (
					fromIndex < toIndex &&
					definition.displayOrder <= toIndex &&
					definition.displayOrder >= fromIndex
				) {
					updatedDefinition.displayOrder--;
				}

				return updatedDefinition;
			});

			props.onDefinitionUpdated(newDefinitions, newDefinition, itemData);
		},
		[props.definitions]
	);

	const makeColumns = () => {
		const columns = props.options.map(option => {
			return (
				<BCDataGrid.Components.Column
					key={option.key}
					name={option.key}
					calculateCellValue={calculateOptionColumnsCellValue}
					setCellValue={setOptionColumnsCellValue}
					caption={MLHelper.getMLText(option.title)}
					headerCellComponent={OptionHeader}
					defaultVisibleIndex={option.displayOrder + (hasColumnButton ? 2 : 1)}
					dataType="boolean"
					allowReordering={!isDisabled}
					minWidth={120}
					editorOptions={{
						hideEditOption: props.hideEditOption,
						hideDeleteOption: props.hideDeleteOption,
						onOptionDeleted: onClickDeleteOption,
						readonly: props.readonly,
						disabled: props.disabled,
						onClickEditOption,
						option
					}}
				/>
			);
		});

		columns.unshift(
			<BCDataGrid.Components.Column
				key="definition_column"
				dataField="title"
				caption=""
				visibleIndex={hasColumnButton ? 1 : 0}
				allowReordering={false}
				width={250}
				calculateDisplayValue={calculateDefinitionColumnsDisplayValue}
				allowEditing={false}
			>
				<BCDataGrid.Components.RequiredRule />
			</BCDataGrid.Components.Column>
		);

		if (hasColumnButton) {
			columns.unshift(
				<BCDataGrid.Components.Column
					key="buttons"
					allowReordering={false}
					visibleIndex={0}
					type="buttons"
					width={80}
				>
					{!props.hideEditDefinition && (
						<BCDataGrid.Components.ColumnButton
							icon={IconEdit.info.getElement()}
							cssClass="dx-link"
							key="edit-definition"
							onClick={onClickEditDefinition}
							visible={!isDisabled}
						/>
					)}
					{!props.hideDeleteDefinition && (
						<BCDataGrid.Components.ColumnButton
							icon={IconDelete.info.getElement()}
							cssClass="dx-link"
							key="delete-definition"
							onClick={onClickDeleteDefinition}
							visible={!isDisabled}
						/>
					)}
				</BCDataGrid.Components.Column>
			);
		}

		return columns;
	};

	const dataSource = React.useMemo(
		() => [...props.definitions.sort((a, b) => a.displayOrder - b.displayOrder)],
		[props.definitions]
	);

	const addOptionButtonOptions = React.useMemo(
		() => ({
			icon: IconAdd.info.getElement(),
			text: findMessage.get('103297'),
			hint: findMessage.get('103297'),
			onClick: onClickAddOption
		}),
		[onClickAddOption]
	);

	const addDefinitionButtonOptions = React.useMemo(
		() => ({
			icon: IconAdd.info.getElement(),
			text: findMessage.get('103298'),
			hint: findMessage.get('103298'),
			onClick: onClickAddDefinition
		}),
		[onClickAddDefinition]
	);

	const isDisabled = props.disabled || props.readonly;

	return (
		<BCDataGrid.Components.DataGrid
			name={`Likert_${props.name}` as any}
			ref={bcDataGridRef}
			keyExpr="key"
			dataSource={dataSource}
			width="100%"
			height={450}
			showColumnLines={false}
			showRowLines
			allowColumnReordering
		>
			<BCDataGrid.Components.RowDragging
				onReorder={onReorderDefinitions}
				allowReordering={!isDisabled}
				showDragIcons
			/>

			<BCDataGrid.Components.Scrolling
				mode="virtual"
				columnRenderingMode="virtual"
			/>
			<BCDataGrid.Components.Sorting mode="none" />
			<BCDataGrid.Components.Editing
				allowAdding={!isDisabled}
				allowUpdating={!isDisabled}
				allowDeleting={!isDisabled && !props.hideDeleteDefinition}
				newRowPosition="last"
				mode="cell"
			/>
			<BCDataGrid.Components.Toolbar>
				<BCDataGrid.Components.Item
					location="before"
					widget="dxButton"
					locateInMenu="auto"
					showText="always"
					visible={!props.hideAddOption}
					disabled={props.maxOptionsCount < props.options.length || isDisabled}
					options={addOptionButtonOptions}
				/>

				<BCDataGrid.Components.Item
					location="before"
					widget="dxButton"
					locateInMenu="auto"
					showText="always"
					visible={!props.hideAddDefinition}
					disabled={props.maxDefinitionsCount < props.definitions.length || isDisabled}
					options={addDefinitionButtonOptions}
				/>
			</BCDataGrid.Components.Toolbar>
			{makeColumns()}
		</BCDataGrid.Components.DataGrid>
	);
};

export { IDefinition, IOption } from './entities';
